import React from 'react';
import { CheckIcon, XMarkIcon, PhotoIcon, LinkIcon, TagIcon, DocumentTextIcon, FolderIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import type { UrlMetadataSuggestion } from '../types/reference';

interface UrlMetadataSuggestionsProps {
  isExtractingMetadata: boolean;
  metadataSuggestions: UrlMetadataSuggestion[];
  extractedThumbnail: string | null;
  metadataError: string | null;
  onApplySuggestion: (index: number) => void;
  onRejectSuggestion: (index: number) => void;
  onApplyAllSuggestions: () => void;
  sourceURL: string;
}

const getFieldIcon = (field: string) => {
  switch (field) {
    case 'title':
      return DocumentTextIcon;
    case 'description':
      return DocumentTextIcon;
    case 'category':
      return FolderIcon;
    case 'tags':
      return TagIcon;
    case 'thumbnail':
      return PhotoIcon;
    default:
      return LinkIcon;
  }
};

const getConfidenceColor = (confidence: string) => {
  switch (confidence) {
    case 'high':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'medium':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'low':
      return 'text-gray-600 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const formatFieldLabel = (field: string) => {
  switch (field) {
    case 'title':
      return 'Title';
    case 'description':
      return 'Description';
    case 'category':
      return 'Category';
    case 'tags':
      return 'Tags';
    case 'thumbnail':
      return 'Thumbnail';
    default:
      return field;
  }
};

const formatSuggestionValue = (field: string, value: string | string[]) => {
  if (field === 'tags' && Array.isArray(value)) {
    return value.join(', ');
  }
  if (field === 'thumbnail' && typeof value === 'string') {
    return 'Thumbnail image found';
  }
  return value as string;
};

// Helper function to safely extract hostname from a URL
const getHostnameFromUrl = (url: string): string | null => {
  try {
    // Try to construct a URL object
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    // If URL is invalid, try to extract domain manually
    try {
      // Remove protocol if present
      const cleanUrl = url.replace(/^https?:\/\//, '');
      // Extract domain part (before first slash or query)
      const domain = cleanUrl.split(/[/?#]/)[0];
      // Basic validation - should contain at least one dot
      if (domain && domain.includes('.')) {
        return domain;
      }
    } catch {
      // If all fails, return null
    }
    return null;
  }
};

export function UrlMetadataSuggestions({
  isExtractingMetadata,
  metadataSuggestions = [],
  extractedThumbnail,
  metadataError,
  onApplySuggestion,
  onRejectSuggestion,
  onApplyAllSuggestions,
  sourceURL
}: UrlMetadataSuggestionsProps) {
  const pendingSuggestions = (metadataSuggestions || []).filter(s => !s.applied);
  const hasPendingSuggestions = pendingSuggestions.length > 0;

  // Don't render anything if there's no URL
  if (!sourceURL) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Loading State */}
      {isExtractingMetadata && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <div>
              <p className="text-sm font-medium text-blue-800">
                Extracting metadata from URL...
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Analyzing {getHostnameFromUrl(sourceURL) || ''} for content information
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {metadataError && !isExtractingMetadata && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <XMarkIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Failed to extract URL metadata
              </p>
              <p className="text-xs text-red-600 mt-1">
                {metadataError}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Banner */}
      {hasPendingSuggestions && !isExtractingMetadata && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <SparklesIcon className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-purple-800">
                  URL Metadata Extracted Successfully
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Found {pendingSuggestions.length} suggestion{pendingSuggestions.length !== 1 ? 's' : ''} to auto-fill your form
                </p>
              </div>
            </div>
            {pendingSuggestions.length > 1 && (
                          <button
              onClick={onApplyAllSuggestions}
              className="btn btn-primary btn-xs"
            >
              Apply All
            </button>
            )}
          </div>
        </div>
      )}

      {/* Individual Suggestions */}
      {(metadataSuggestions || []).map((suggestion, index) => {
        if (suggestion.applied) return null;

        const IconComponent = getFieldIcon(suggestion.field);
        const confidenceStyle = getConfidenceColor(suggestion.confidence);

        return (
          <div
            key={`${suggestion.field}-${index}`}
            className={`border rounded-lg p-4 ${confidenceStyle}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <IconComponent className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium">
                      {formatFieldLabel(suggestion.field)}
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/50">
                      {suggestion.confidence} confidence
                    </span>
                  </div>
                  
                  {suggestion.field === 'thumbnail' && extractedThumbnail ? (
                    <div className="mt-2">
                      <img
                        src={extractedThumbnail}
                        alt="Extracted thumbnail"
                        className="w-24 h-16 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-sm mt-1 break-words">
                      {formatSuggestionValue(suggestion.field, suggestion.value)}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onApplySuggestion(index)}
                  className="btn btn-info btn-xs"
                  title="Apply this suggestion"
                >
                  Apply
                </button>
                                  <button
                    onClick={() => onRejectSuggestion(index)}
                    className="btn btn-neutral btn-xs"
                    title="Reject this suggestion"
                  >
                    Reject
                  </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
} 